<template>
    <eSesjaModal>
        <eSesjaModalHeader @close="closeModal">
            {{ $t("com.global.add") }} {{ $t("com.discussion.profile.modes") }}
        </eSesjaModalHeader>
        <eSesjaModalBody>
            <section class="discussion-profile-mode-form" v-if="newMode && availableTypesForAdd.length">
                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-type") }}</label>
                    <select v-model.number="newMode.typeId" class="edit-point-form__input" @change="syncNewTypeName">
                        <option v-for="type in availableTypesForAdd" :key="type.id" :value="type.id">
                            {{ type.name }}
                        </option>
                    </select>
                </section>

                <section class="discussion-profile-mode-form__row">
                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-limit-group") }}</label>
                    <select v-model="newMode.limitGroupId" class="edit-point-form__input" :disabled="newMode.isBlock">
                        <option :value="currentLimitGroupValue">{{ $t("com.discussion.profile.mode-limit-group.no-group") }}</option>
                        <option v-for="option in availableLimitGroupModesForAdd" :key="`new-limit-group-${option.id}`" :value="option.id">
                            {{ option.type || "-" }}
                        </option>
                    </select>
                </section>

                <template v-if="!isGroupedLimitSelected">
                    <section class="discussion-profile-mode-form__row">
                        <section class="discussion-profile-mode-form__switch-row">
                            <InputSwitch id="new-mode-block-modal" v-model="newMode.isBlock" />
                            <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-mode-block-modal">
                                {{ $t("com.discussion.profile.mode-block") }}
                            </label>
                        </section>
                    </section>
                    <section class="discussion-profile-mode-form__row">
                        <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-limit-time-type") }}</label>
                        <section class="discussion-profile-mode-form__type-groups">
                            <section
                                class="discussion-profile-mode-form__type-group"
                                :class="{ 'discussion-profile-mode-form__type-group--disabled': isStandardStatementDisabled }"
                            >
                                <section class="discussion-profile-mode-form__switch-row">
                                    <InputSwitch
                                        id="new-limit-time-type-1"
                                        :model-value="newMode.limitTimeType === 1"
                                        @update:modelValue="setLimitTimeType(1, $event)"
                                    />
                                    <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-limit-time-type-1">
                                        {{ $t("com.discussion.profile.mode-limit-time-type.fixed") }}
                                    </label>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-first-statement") }}</label>
                                    <select v-model.number="newMode.firstStatement" class="edit-point-form__input" :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`new-first-${option.id}`" :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-second-statement") }}</label>
                                    <select v-model.number="newMode.secondStatement" class="edit-point-form__input" :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`new-second-${option.id}`" :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-third-statement") }}</label>
                                    <select v-model.number="newMode.thirdStatement" class="edit-point-form__input" :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`new-third-${option.id}`" :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-next-statement") }}</label>
                                    <select v-model.number="newMode.nextStatement" class="edit-point-form__input" :disabled="isStandardStatementDisabled">
                                        <option v-for="option in statementTimeOptions" :key="`new-next-${option.id}`" :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>
                            </section>

                            <section
                                class="discussion-profile-mode-form__type-group"
                                :class="{ 'discussion-profile-mode-form__type-group--disabled': isElasticStatementDisabled && isRequestLimitDisabled }"
                            >
                                <section class="discussion-profile-mode-form__switch-row">
                                    <InputSwitch
                                        id="new-limit-time-type-2"
                                        :model-value="newMode.limitTimeType === 2"
                                        @update:modelValue="setLimitTimeType(2, $event)"
                                    />
                                    <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-limit-time-type-2">
                                        {{ $t("com.discussion.profile.mode-limit-time-type.flex") }}
                                    </label>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-elastic-statement") }}</label>
                                    <select v-model.number="newMode.elasticStatement" class="edit-point-form__input" :disabled="isElasticStatementDisabled" @change="handleElasticStatementChange">
                                        <option v-for="option in statementTimeOptions" :key="`new-elastic-${option.id}`" :value="option.id">
                                            {{ $t(option.textKey) }}
                                        </option>
                                    </select>
                                </section>

                                <section class="discussion-profile-mode-form__row">
                                    <label class="edit-point-form__label">{{ $t("com.discussion.profile.mode-request-limit") }}</label>
                                    <select v-model="newMode.requestLimitId" class="edit-point-form__input" :disabled="isRequestLimitDisabled" @change="handleRequestLimitChange">
                                        <option v-for="option in requestLimitTypes" :key="`new-request-limit-${option.id}`" :value="option.id">
                                            {{ $t(option.name) }}
                                        </option>
                                    </select>
                                </section>
                            </section>
                        </section>
                    </section>

                    <section class="discussion-profile-mode-form__row">
                        <section class="discussion-profile-mode-form__switch-row">
                            <InputSwitch id="new-mode-turn-off-modal" v-model="newMode.turnOff" :disabled="isTurnOffDisabled" />
                            <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-mode-turn-off-modal">
                                {{ $t("com.discussion.profile.mode-turn-off") }}
                            </label>
                        </section>
                    </section>
                    <section class="discussion-profile-mode-form__row">
                        <section class="discussion-profile-mode-form__switch-row">
                            <InputSwitch id="new-mode-sound-modal" v-model="newMode.isSoundNotification" :disabled="isSoundNotificationDisabled" />
                            <label class="edit-point-form__label discussion-profile-mode-form__switch-label" for="new-mode-sound-modal">
                                {{ $t("com.discussion.profile.mode-sound-notification") }}
                            </label>
                        </section>
                    </section>
                </template>
                <section v-else class="discussion-profile-mode-form__group-message">
                    {{ $t("com.discussion.profile.mode-limit-group-inherited-info") }}
                </section>
            </section>
            <section v-else class="text-center">
                {{ $t("com.global.no-data") }}
            </section>
        </eSesjaModalBody>
        <eSesjaModalFooter>
            <eSesjaModalButtonCancel @close="closeModal">
                {{ $t("com.global.cancel") }}
            </eSesjaModalButtonCancel>
            <eSesjaModalButtonSave :disabled="isSaveDisabled" @save="createMode">
                {{ $t("com.global.save") }}
            </eSesjaModalButtonSave>
        </eSesjaModalFooter>
    </eSesjaModal>
</template>

<script charset="utf-8">
import AlertHelper from "@/helpers/alerts.helper.js";
import DiscussionProfileService from "@/services/discussion.profile.service";
import InputSwitch from "@/components/BasicControls/InputSwitch.vue";

export default {
    name: "AddDiscussionProfileModeModal",
    components: {
        InputSwitch
    },
    props: {
        discussionId: {
            type: Number,
            required: true
        },
        statementTypes: {
            type: Array,
            default: () => []
        },
        statementTimeOptions: {
            type: Array,
            default: () => []
        },
        limitTimeTypeOptions: {
            type: Array,
            default: () => []
        },
        requestLimitTypes: {
            type: Array,
            default: () => []
        },
        availableLimitGroupModes: {
            type: Array,
            default: () => []
        },
        usedTypeIds: {
            type: Array,
            default: () => []
        }
    },
    emits: ["close", "created"],
    data() {
        return {
            isSaving: false,
            newMode: null
        };
    },
    computed: {
        availableTypesForAdd() {
            return this.statementTypes.filter((type) => !this.usedTypeIds.includes(type.id));
        },
        availableLimitGroupModesForAdd() {
            return this.availableLimitGroupModes.filter((mode) => {
                const modeId = Number(mode?.id);
                const limitGroupId = this.normalizeNullableNumber(mode?.limitGroupId);

                return modeId > 0 && limitGroupId === modeId;
            });
        },
        currentLimitGroupValue() {
            return this.newMode?.id ?? null;
        },
        defaultElasticStatement() {
            return -1;
        },
        defaultRequestLimitId() {
            return this.requestLimitTypes.length > 0 ? Number(this.requestLimitTypes[0].id) : 1;
        },
        isGroupedLimitSelected() {
            return this.newMode && this.newMode.limitGroupId !== null && Number(this.newMode.limitGroupId) !== Number(this.currentLimitGroupValue);
        },
        hasCustomElasticStatement() {
            return !!this.newMode && this.normalizeNumber(this.newMode.elasticStatement, this.defaultElasticStatement) !== this.defaultElasticStatement;
        },
        hasCustomRequestLimit() {
            return !!this.newMode && this.normalizeNumber(this.newMode.requestLimitId, this.defaultRequestLimitId) !== this.defaultRequestLimitId;
        },
        hasActiveLimitTimeType() {
            return this.isFixedLimitTimeTypeActive || this.isFlexLimitTimeTypeActive;
        },
        isFixedLimitTimeTypeActive() {
            return Number(this.newMode?.limitTimeType) === 1;
        },
        isFlexLimitTimeTypeActive() {
            return Number(this.newMode?.limitTimeType) === 2;
        },
        isLimitTimeTypeInteractionDisabled() {
            return this.newMode?.isBlock || this.isGroupedLimitSelected;
        },
        isFixedLimitTimeTypeSwitchDisabled() {
            return this.isLimitTimeTypeInteractionDisabled || this.isFlexLimitTimeTypeActive;
        },
        isFlexLimitTimeTypeSwitchDisabled() {
            return this.isLimitTimeTypeInteractionDisabled || this.isFixedLimitTimeTypeActive;
        },
        isStandardStatementDisabled() {
            return this.newMode?.isBlock || this.isGroupedLimitSelected || !this.isFixedLimitTimeTypeActive;
        },
        isElasticStatementDisabled() {
            return this.newMode?.isBlock || this.isGroupedLimitSelected || !this.isFlexLimitTimeTypeActive;
        },
        isRequestLimitDisabled() {
            return this.newMode?.isBlock || this.isGroupedLimitSelected || !this.isFlexLimitTimeTypeActive;
        },
        isTurnOffDisabled() {
            return this.newMode?.isBlock || this.isGroupedLimitSelected;
        },
        isSoundNotificationDisabled() {
            return this.newMode?.isBlock || this.isGroupedLimitSelected;
        },
        isSaveDisabled() {
            return this.isSaving || !this.newMode || this.newMode.typeId <= 0 || !this.hasActiveLimitTimeType;
        }
    },
    mounted() {
        this.resetForm();
    },
    methods: {
        createDefaultModeModel() {
            const firstAvailableType = this.availableTypesForAdd[0];

            return {
                type: firstAvailableType?.name || "",
                typeId: firstAvailableType?.id || 0,
                isBlock: false,
                firstStatement: 60,
                secondStatement: 60,
                thirdStatement: 60,
                nextStatement: 60,
                limitTimeType: 1,
                limitGroupId: null,
                elasticStatement: -1,
                requestLimitId: 1,
                turnOff: false,
                isSoundNotification: false
            };
        },
        resetForm() {
            this.newMode = this.createDefaultModeModel();
            this.normalizeExclusiveFields();
        },
        syncNewTypeName() {
            if (!this.newMode) {
                return;
            }

            const selectedType = this.statementTypes.find((type) => type.id === this.newMode.typeId);
            this.newMode.type = selectedType?.name || "";
        },
        setLimitTimeType(typeId, isEnabled) {
            if (!this.newMode) {
                return;
            }

            if (isEnabled) {
                this.newMode.limitTimeType = typeId;
                return;
            }
            else {
                if (typeId === 1) {
                    this.newMode.limitTimeType = 2;
                }
                else if (typeId === 2) {
                    this.newMode.limitTimeType = 1;
                }
            }

            if (Number(this.newMode.limitTimeType) === typeId) {
                this.newMode.limitTimeType = null;
            }
        },
        handleElasticStatementChange() {
            if (!this.newMode) {
                return;
            }

            this.newMode.elasticStatement = this.normalizeNumber(this.newMode.elasticStatement, this.defaultElasticStatement);
        },
        handleRequestLimitChange() {
            if (!this.newMode) {
                return;
            }

            this.newMode.requestLimitId = this.normalizeNumber(this.newMode.requestLimitId, this.defaultRequestLimitId);
        },
        normalizeExclusiveFields() {
            if (!this.newMode) {
                return;
            }

            this.newMode.elasticStatement = this.normalizeNumber(this.newMode.elasticStatement, this.defaultElasticStatement);
            this.newMode.requestLimitId = this.normalizeNumber(this.newMode.requestLimitId, this.defaultRequestLimitId);

            if (this.newMode.elasticStatement !== this.defaultElasticStatement && this.newMode.requestLimitId !== this.defaultRequestLimitId) {
                this.newMode.requestLimitId = this.defaultRequestLimitId;
            }
        },
        normalizeNumber(value) {
            const parsed = Number(value);

            if (!Number.isFinite(parsed)) {
                return 0;
            }

            return parsed;
        },
        normalizeNullableNumber(value) {
            if (value === null || value === undefined || value === "") {
                return null;
            }

            const parsed = Number(value);

            return Number.isFinite(parsed) ? parsed : null;
        },
        closeModal() {
            this.$emit("close");
        },
        async createMode() {
            if (this.isSaveDisabled) {
                return;
            }

            try {
                this.isSaving = true;

                await DiscussionProfileService.AddMode(this.discussionId, {
                    type: this.newMode.type,
                    typeId: this.newMode.typeId,
                    isBlock: this.newMode.isBlock,
                    firstStatement: this.normalizeNumber(this.newMode.firstStatement),
                    secondStatement: this.normalizeNumber(this.newMode.secondStatement),
                    thirdStatement: this.normalizeNumber(this.newMode.thirdStatement),
                    nextStatement: this.normalizeNumber(this.newMode.nextStatement),
                    limitTimeType: this.normalizeNumber(this.newMode.limitTimeType) || 1,
                    limitGroupId: this.normalizeNullableNumber(this.newMode.limitGroupId),
                    elasticStatement: Number(this.newMode.limitTimeType) === 2 ? this.normalizeNumber(this.newMode.elasticStatement) : 0,
                    requestLimitId: this.normalizeNullableNumber(this.newMode.requestLimitId),
                    turnOff: this.newMode.turnOff,
                    isSoundNotification: this.newMode.isSoundNotification
                });

                this.$emit("created");
                this.closeModal();
            } catch (e) {
                if (e.response !== undefined) {
                    const t = this.$swal.mixin(AlertHelper.errorToastElement());
                    t.fire(
                        AlertHelper.errorToastAlert(
                            this.$t("com.global.default-save-error"),
                            this.$t("com.global.error")
                        )
                    );
                }
            } finally {
                this.isSaving = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.discussion-profile-mode-form {
    display: flex;
    flex-direction: column;
    row-gap: 16px;

    &__row {
        display: flex;
        flex-direction: column;
    }

    &__switch-row {
        display: flex;
        align-items: center;
        column-gap: 12px;
        min-height: 38px;
    }

    &__switch-label {
        margin: 0;
    }

    &__type-groups {
        display: flex;
        gap: 16px;
        margin-top: 8px;
        align-items: flex-start;
    }

    &__type-group {
        flex: 1 1 280px;
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        padding: 12px;
        border: 1px solid #d1d6df;
        border-radius: 4px;
        background: #ffffff;
    }

    &__type-group--disabled {
        background: #f3f4f6;
        border-color: #d8dce5;
    }

    &__group-message {
        padding: 12px 16px;
        border: 1px solid #d8dce5;
        border-radius: 4px;
        background: #f3f4f6;
        color: #5c6377;
        font-size: 14px;
        line-height: 1.45;
    }

    &__option-switches {
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        margin-top: 8px;
    }
}

.edit-point-form__input {
    width: 100%;
    padding: 12.5px 16px;
    background: #ffffff;
    border: 1px solid #d1d6df;
    box-sizing: border-box;
    box-shadow: 1px 1px 3px rgba(0, 8, 44, 0.06);
    border-radius: 4px;
    margin: 8px 0px;
    font-size: 16px;
    line-height: 144%;
    color: #00082c;

    &:focus {
        border: 1px solid #00082c;
    }
}

.edit-point-form__label {
    font-weight: 600;
    font-size: 14px;
    line-height: 145%;
    color: #00082c;
}

.mode-switch {
    display: flex;
    justify-content: center;
}
</style>
